import { Fragment } from 'react'
import {
  Box,
  Table,
  HStack,
  VStack,
  Text,
  Icon,
  IconButton,
  Menu,
  Spinner,
  Portal,
} from '@chakra-ui/react';
import { FiMoreVertical } from 'react-icons/fi'
import { useIsMobile } from '../../hooks/useIsMobile'
import { Card } from './Card'

export type MobileRole = 'title' | 'meta' | 'badge' | 'desktop-only'

export interface ResponsiveColumn<T> {
  header: string
  cell: (row: T, index: number) => React.ReactNode
  mobileRole?: MobileRole
  isNumeric?: boolean
  width?: string
}

export interface RowAction<T> {
  label: string
  icon: React.ElementType
  onClick: (row: T) => void
  /** @deprecated back-compat only — prefer `destructive` for delete/remove actions. */
  color?: string
  /** Marks a delete/remove-style action: neutral at rest, `danger.fg` on hover. */
  destructive?: boolean
  isLoading?: (row: T) => boolean
  /** Omit this action for rows where it returns true (per-row gating). */
  hidden?: (row: T) => boolean
}

export interface ResponsiveTableProps<T> {
  columns: ResponsiveColumn<T>[]
  rows: T[]
  keyFn: (row: T) => string
  onRowClick?: (row: T) => void
  rowActions?: RowAction<T>[]
  emptyMessage?: string
  isLoading?: boolean
  /**
   * Drop the desktop table's own white card surface so it sits flush inside an
   * existing card (e.g. a detail-page section). Avoids card-in-card nesting.
   * The mobile card-list treatment is unaffected.
   */
  bare?: boolean
  /**
   * L11 slice 1e, Task 17: the key (per `keyFn`) of the row currently expanded, or
   * `null`/`undefined` when none is. When set, `renderExpanded(row)` is rendered directly
   * below that row -- a full-width extra `Table.Row` on desktop, appended inside the same
   * card on mobile. Generic + opt-in so existing consumers are unaffected.
   */
  expandedRowKey?: string | null
  renderExpanded?: (row: T) => React.ReactNode
}

export function ResponsiveTable<T>({
  columns, rows, keyFn, onRowClick, rowActions, emptyMessage = 'No results.', isLoading, bare,
  expandedRowKey, renderExpanded,
}: ResponsiveTableProps<T>) {
  const isMobile = useIsMobile()

  if (isLoading) {
    return (
      <VStack py={12}>
        <Spinner size="lg" color="accent" />
      </VStack>
    )
  }

  if (rows.length === 0) {
    if (bare) {
      return (
        <Box py={8} textAlign="center" color="muted">
          {emptyMessage}
        </Box>
      )
    }
    return (
      <Card p={8} textAlign="center" color="muted">
        {emptyMessage}
      </Card>
    )
  }

  const hasActions = !!rowActions && rowActions.length > 0
  const visibleActions = (row: T) => (rowActions ?? []).filter((a) => !a.hidden?.(row))

  // ---------- Mobile: card list ----------
  if (isMobile) {
    const titleCols = columns.filter((c) => c.mobileRole === 'title')
    const badgeCols = columns.filter((c) => c.mobileRole === 'badge')
    const metaCols = columns.filter((c) => (c.mobileRole ?? 'meta') === 'meta')
    return (
      <VStack align="stretch" gap={3}>
        {rows.map((row, rowIndex) => (
          <Box
            key={keyFn(row)}
            bg="surface"
            borderRadius="lg"
            shadow="sm"
            p={4}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            cursor={onRowClick ? 'pointer' : 'default'}
            _active={onRowClick ? { bg: 'surface.2' } : undefined}
          >
            <HStack align="start" justify="space-between" gap={2}>
              <VStack align="stretch" gap={1} flex={1} minW={0}>
                {titleCols.map((c, i) => (
                  <Box key={c.header} fontWeight={i === 0 ? 'semibold' : 'normal'} fontSize={i === 0 ? 'md' : 'sm'}>
                    {c.cell(row, rowIndex)}
                  </Box>
                ))}
                {badgeCols.length > 0 && (
                  <HStack gap={2} flexWrap="wrap">
                    {badgeCols.map((c) => <Box key={c.header}>{c.cell(row, rowIndex)}</Box>)}
                  </HStack>
                )}
                {metaCols.map((c) => (
                  <HStack key={c.header} justify="space-between" gap={3}>
                    <Text fontSize="xs" color="muted">{c.header}</Text>
                    {c.isNumeric ? (
                      <Box
                        data-numeric
                        fontFamily="mono"
                        css={{ fontVariantNumeric: 'tabular-nums' }}
                        fontSize="sm"
                        textAlign="right"
                      >
                        {c.cell(row, rowIndex)}
                      </Box>
                    ) : (
                      <Box fontSize="sm" textAlign="right">{c.cell(row, rowIndex)}</Box>
                    )}
                  </HStack>
                ))}
              </VStack>
              {hasActions && visibleActions(row).length > 0 && (
                <Menu.Root positioning={{
                  placement: 'bottom-end'
                }}>
                  <Menu.Trigger asChild><IconButton
                      aria-label="Row actions"
                      variant="ghost"
                      size="sm"
                      minW="44px"
                      minH="44px"
                      onClick={(e) => e.stopPropagation()}><FiMoreVertical /></IconButton></Menu.Trigger>
                  <Portal><Menu.Positioner><Menu.Content onClick={(e) => e.stopPropagation()}>
                        {visibleActions(row).map((a) => {
                          const loading = a.isLoading?.(row)
                          return (
                            <Menu.Item
                              key={a.label}
                              value={a.label}
                              color={a.destructive ? 'danger.fg' : a.color}
                              data-destructive={a.destructive ? 'true' : undefined}
                              disabled={loading}
                              onSelect={() => a.onClick(row)}>
                              <HStack gap={2}>
                                {loading ? <Spinner size="xs" /> : <Icon asChild><a.icon /></Icon>}
                                <Text>{a.label}</Text>
                              </HStack>
                            </Menu.Item>
                          );
                        })}
                      </Menu.Content></Menu.Positioner></Portal>
                </Menu.Root>
              )}
            </HStack>
            {expandedRowKey === keyFn(row) && renderExpanded && (
              <Box mt={3} onClick={(e) => e.stopPropagation()}>{renderExpanded(row)}</Box>
            )}
          </Box>
        ))}
      </VStack>
    );
  }

  // ---------- Desktop: table ----------
  return (
    <Box
      overflowX="auto"
      {...(bare ? {} : { bg: 'surface', borderRadius: 'lg', shadow: 'md' })}
    >
      <Table.Root size="sm">
        <Table.Header>
          <Table.Row>
            {columns.map((c) => (
              <Table.ColumnHeader key={c.header} width={c.width} textAlign={c.isNumeric ? 'end' : undefined}>{c.header}</Table.ColumnHeader>
            ))}
            {hasActions && <Table.ColumnHeader />}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.map((row, rowIndex) => (
            <Fragment key={keyFn(row)}>
              <Table.Row
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                _hover={onRowClick ? { bg: 'surface.2', cursor: 'pointer' } : undefined}
              >
                {columns.map((c) => (
                  <Table.Cell
                    key={c.header}
                    textAlign={c.isNumeric ? 'end' : undefined}
                    data-numeric={c.isNumeric ? true : undefined}
                    fontFamily={c.isNumeric ? 'mono' : undefined}
                    css={c.isNumeric ? { fontVariantNumeric: 'tabular-nums' } : undefined}
                  >
                    {c.cell(row, rowIndex)}
                  </Table.Cell>
                ))}
                {hasActions && (
                  <Table.Cell textAlign="right" onClick={(e) => e.stopPropagation()}>
                    <HStack justify="flex-end" gap={1}>
                      {visibleActions(row).map((a) => (
                        <IconButton
                          key={a.label}
                          aria-label={a.label}
                          variant="ghost"
                          size="sm"
                          color={a.destructive ? undefined : a.color}
                          data-destructive={a.destructive ? 'true' : undefined}
                          _hover={a.destructive ? { color: 'danger.fg' } : undefined}
                          loading={a.isLoading?.(row)}
                          onClick={(e) => { e.stopPropagation(); a.onClick(row) }}><Icon asChild><a.icon /></Icon></IconButton>
                      ))}
                    </HStack>
                  </Table.Cell>
                )}
              </Table.Row>
              {expandedRowKey === keyFn(row) && renderExpanded && (
                <Table.Row>
                  <Table.Cell colSpan={columns.length + (hasActions ? 1 : 0)} p={0} onClick={(e) => e.stopPropagation()}>
                    {renderExpanded(row)}
                  </Table.Cell>
                </Table.Row>
              )}
            </Fragment>
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  );
}
